'use client'

import React from 'react';

//Partials
import Section from "@/components/partials/section";
import Card from "@/components/partials/card";

//Heroicons
import { ArrowDownOnSquareIcon, ArrowDownOnSquareStackIcon, ArrowRightCircleIcon, FolderIcon } from "@heroicons/react/24/solid";

//Types
import Button from "@/components/partials/button";
import type { CardItem } from '@/types/card-item.type';

type SpecializeContentButton = {
    text: string;
    url: string;
    type?: string;
    target?: string;
};

type SpecializeContentCard = CardItem;

type SpecializeContent = {
    buttons?: SpecializeContentButton[];
    cards?: SpecializeContentCard[];
};

type SpecializeBlockProps = {
    data: {
        alignment: string,
        leading: string;
        title: string;
        description: string;
        content: SpecializeContent[];
    };
};

const SpecializeBlock = ({ data }: SpecializeBlockProps) => {

    const { leading, alignment, title, description, content } = data;
    const buttons = content.flatMap(c => c.buttons || []);
    const cards = content.flatMap(c => c.cards || []);

    return (
        <Section
            id={SpecializeBlock.name}
            header={{
                classGroup: {
                    h1: "text-3xl sm:text-6xl",
                },
                leading,
                alignment: alignment,
                title,
                description,
            }}
            action={
                buttons.length > 0 && (
                    <div className={`flex items-center gap-4 ${alignment === 'center' ? 'flex-col md:items-center' : alignment === 'right' ? 'flex-col md:items-end' : 'flex-col md:items-start md:flex-row'}`}>
                        {buttons.map((btn, index) => (
                            <Button
                                key={index}
                                id={`${SpecializeBlock.name}-${btn.text.replace(/\s+/g, '')}`}
                                href={btn.url}
                                type={btn.type || 'contained'}
                                target={btn.target || '_self'}
                                className="flex text-sm md:text-lg"
                                ariaLabel={btn.text}
                                icon={
                                    btn.text.toLowerCase().includes("schedule") ||
                                        btn.text.toLowerCase().includes("book") ? (
                                        <ArrowRightCircleIcon className="flex-shrink-0 w-5 h-5 ms-4 -rotate-45 md:w-7 md:h-7" />
                                    ) : btn.text.toLowerCase().includes("download") ? (
                                        <ArrowDownOnSquareStackIcon className="flex-shrink-0 w-5 h-5 ms-4 md:w-7 md:h-7" />
                                    ) : btn.text.toLowerCase().includes("view") ? (
                                        <FolderIcon className="flex-shrink-0 w-5 h-5 ms-4 md:w-7 md:h-7" />
                                    ) : btn.text.toLowerCase().includes("custom") ? (
                                        <svg className="flex-shrink-0 w-5 h-5 ms-4 md:w-7 md:h-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l9 9-9 9-9-9 9-9z" />
                                        </svg>
                                    ) : undefined
                                }
                                iconPosition="right"
                            >
                                {btn.text}
                            </Button>
                        ))}
                    </div>
                )
            }
        >
            <Card
                id={SpecializeBlock.name + `-cards`}
                cardClassName="flex-col p-6 bg-[var(--background2-color)] rounded-lg border-2 border-[var(--border-color)] hover:shadow-md transition"
                ariaLabel="{title}"
                cardGrid={4}
                content={cards}
            />
        </Section>
    );
};

export default SpecializeBlock;
